import { useMemo, useState } from 'react';
import { Field, Note } from './components.js';
import { Logo } from './Logo.js';

/**
 * The screen an account sees when it owes a password change.
 *
 * PCI DSS 8.3.5 and 8.3.9 mean two kinds of account arrive here: one whose
 * password was generated by an administrator and has therefore been read by
 * somebody other than its owner, and one whose password is the only factor and
 * has passed ninety days. In both cases the server has already decided that the
 * session authenticates but authorises nothing, so this is not a screen the user
 * can navigate away from - it is the only thing the session can do.
 *
 * Shared between the admin console and the merchant panel because the rule is
 * the same on both, and two copies of a security screen is one copy too many.
 */

/** Mirrors `POLICY` in the API, which is the authority. */
const MIN_LENGTH = 12;

interface Rule {
  label: string;
  met: boolean;
}

export function PasswordChange({
  reason,
  onSubmit,
  onCancel,
}: {
  /**
   * Why the change is being demanded. `expired` and `assigned` are the two the
   * standard produces; `voluntary` is someone choosing to change it from the
   * settings screen, where cancelling is allowed.
   */
  reason: 'assigned' | 'expired' | 'voluntary';
  onSubmit: (current: string, next: string) => Promise<void>;
  onCancel?: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Shown as you type rather than only after a failed submit. The server is
   * still the authority - it also refuses the previous four passwords, which
   * the browser has no way to know - but there is no reason to make someone
   * submit a form to be told it is too short.
   */
  const rules: Rule[] = useMemo(
    () => [
      { label: `Поне ${MIN_LENGTH} знака`, met: next.length >= MIN_LENGTH },
      { label: 'Съдържа буква', met: /\p{L}/u.test(next) },
      { label: 'Съдържа цифра', met: /\p{Nd}/u.test(next) },
      { label: 'Различна от текущата', met: next.length > 0 && next !== current },
    ],
    [next, current],
  );

  const matches = next.length > 0 && next === repeat;
  const ready = rules.every((r) => r.met) && matches && current.length > 0;

  const heading =
    reason === 'assigned' ? 'Изберете своя парола'
    : reason === 'expired' ? 'Паролата ви изтече'
    : 'Смяна на паролата';

  const explanation =
    reason === 'assigned' ?
      'Тази парола е създадена от администратор и е била видяна от друг човек. Докато не я смените, профилът ви влиза, но не може да прави нищо друго.'
    : reason === 'expired' ?
      'Профилът ви се защитава само с парола, а тази е на повече от 90 дни. Изберете нова, за да продължите.'
    : null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(current, next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Паролата не беше сменена.');
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20 }}>
      <form
        onSubmit={submit}
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--wc-surface)',
          border: '1px solid var(--wc-border)',
          borderRadius: 10,
          padding: 26,
        }}
      >
        <div style={{ marginBottom: 18 }}>
          <Logo height={56} />
        </div>

        <h1 style={{ fontSize: 19, margin: '0 0 6px' }}>{heading}</h1>
        {explanation ? (
          <p style={{ margin: '0 0 18px', color: 'var(--wc-muted)', fontSize: 13.5, lineHeight: 1.5 }}>
            {explanation}
          </p>
        ) : null}

        {error ? (
          <div style={{ marginBottom: 14 }}>
            <Note tone="danger">{error}</Note>
          </div>
        ) : null}

        <Field label="Текуща парола">
          <input
            type="password"
            className="wc-input"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </Field>

        <Field label="Нова парола">
          <input
            type="password"
            className="wc-input"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
        </Field>

        <ul
          style={{
            listStyle: 'none',
            margin: '2px 0 14px',
            padding: 0,
            display: 'grid',
            gap: 3,
            fontSize: 12.5,
          }}
        >
          {rules.map((rule) => (
            <li
              key={rule.label}
              style={{ color: rule.met ? 'var(--wc-ok-ink, #15803d)' : 'var(--wc-faint)' }}
            >
              <span aria-hidden style={{ marginRight: 6 }}>{rule.met ? '✓' : '·'}</span>
              {rule.label}
            </li>
          ))}
        </ul>

        <Field
          label="Повторете новата парола"
          hint={
            repeat.length > 0 && !matches ? (
              <span style={{ color: 'var(--wc-danger-ink, #b91c1c)' }}>Двете не съвпадат.</span>
            ) : null
          }
        >
          <input
            type="password"
            className="wc-input"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            required
          />
        </Field>

        <p style={{ margin: '4px 0 16px', color: 'var(--wc-faint)', fontSize: 12.5, lineHeight: 1.5 }}>
          Не може да е някоя от последните четири, които сте ползвали. След смяната всички ваши сесии
          се прекратяват и влизате отново.
        </p>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" className="wc-btn wc-btn-primary" disabled={!ready || busy}>
            {busy ? 'Записва се…' : 'Смени паролата'}
          </button>
          {onCancel ? (
            <button type="button" className="wc-btn" onClick={onCancel} disabled={busy}>
              Откажи
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
